const APP_PROXY_ENDPOINT = '/apps/yourasset/checkout-attempts';
const YOURASSET_HOST_SUFFIX = '.yourasset.com';
const YOURASSET_THRESHOLD_CENTS = 760000;
const initializedRoots = new WeakSet();

export function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

function cookie(name, source = document.cookie) {
  const prefix = `${encodeURIComponent(name)}=`;
  const match = source.split(/;\s*/).find((entry) => entry.startsWith(prefix));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(prefix.length));
  } catch {
    return match.slice(prefix.length);
  }
}

export function consent(customerPrivacy = globalThis.Shopify?.customerPrivacy) {
  return {
    analytics: Boolean(customerPrivacy?.analyticsProcessingAllowed?.()),
    marketing: Boolean(customerPrivacy?.marketingAllowed?.()),
  };
}

let customerPrivacyPromise = null;

function loadCustomerPrivacy() {
  if (globalThis.Shopify?.customerPrivacy) {
    return Promise.resolve(globalThis.Shopify.customerPrivacy);
  }
  if (typeof globalThis.Shopify?.loadFeatures !== 'function') return Promise.resolve(null);
  customerPrivacyPromise ||= new Promise((resolve) => {
    globalThis.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => resolve(error ? null : globalThis.Shopify.customerPrivacy || null),
    );
  });
  return customerPrivacyPromise;
}

function attribution(consentState, locationUrl, storage, cookieSource) {
  const parameters = new URL(locationUrl).searchParams;
  const analytics = consentState.analytics;
  const marketing = consentState.marketing;
  return {
    edgeTagSessionId: analytics ? storage.getItem('tag_session') : null,
    edgeTagVisitorId: analytics
      ? parameters.get('et_u_id') || parameters.get('tag_user_id') || parameters.get('_glc[tag_user_id]')
      : null,
    fbclid: marketing ? parameters.get('fbclid') : null,
    fbc: marketing ? cookie('_fbc', cookieSource) : null,
    fbp: marketing ? cookie('_fbp', cookieSource) : null,
    gbraid: marketing ? parameters.get('gbraid') : null,
    gclid: marketing ? parameters.get('gclid') : null,
    shopifyClientId: null,
    utmCampaign: marketing ? parameters.get('utm_campaign') : null,
    utmContent: marketing ? parameters.get('utm_content') : null,
    utmMedium: marketing ? parameters.get('utm_medium') : null,
    utmSource: marketing ? parameters.get('utm_source') : null,
    utmTerm: marketing ? parameters.get('utm_term') : null,
    wbraid: marketing ? parameters.get('wbraid') : null,
  };
}

export function buildAttemptPayload(dataset, quantity, context) {
  const unitPriceCents = Number.parseInt(dataset.priceCents, 10);
  if (!Number.isSafeInteger(unitPriceCents) || unitPriceCents <= 0) {
    throw new Error('Invalid product price');
  }
  const safeQuantity = Number.isSafeInteger(quantity) && quantity > 0 ? quantity : 1;
  const unitPrice = (unitPriceCents / 100).toFixed(2);
  const pageUrl = new URL(dataset.productUrl, context.locationUrl);
  pageUrl.hash = '';
  pageUrl.search = '';
  return {
    attribution: attribution(
      context.consent,
      context.locationUrl,
      context.storage,
      context.cookieSource,
    ),
    consent: context.consent,
    currency: dataset.currency,
    idempotencyKey: context.idempotencyKey,
    items: [{
      productId: dataset.productId,
      quantity: safeQuantity,
      sku: dataset.sku,
      title: dataset.title,
      unitPrice,
      variantId: dataset.variantId || null,
    }],
    pageUrl: pageUrl.href,
    shopifyCartId: null,
    shopifyCartToken: null,
    sourceSurface: 'pdp',
    totalAmount: ((unitPriceCents * safeQuantity) / 100).toFixed(2),
  };
}

export function buildCartAttemptPayload(dataset, items, context) {
  const totalPriceCents = Number.parseInt(dataset.priceCents, 10);
  if (!Number.isSafeInteger(totalPriceCents) || totalPriceCents <= 0) {
    throw new Error('Invalid cart total');
  }
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Invalid cart items');
  }

  const normalizedItems = items.flatMap((item) => {
    const unitPriceCents = Number.parseInt(item.unitPriceCents, 10);
    const quantity = Number.parseInt(item.quantity, 10);
    const suppliedLinePriceCents = Number.parseInt(item.linePriceCents, 10);
    const linePriceCents = Number.isSafeInteger(suppliedLinePriceCents)
      ? suppliedLinePriceCents
      : unitPriceCents * quantity;
    if (!Number.isSafeInteger(unitPriceCents) || unitPriceCents < 0
      || !Number.isSafeInteger(quantity) || quantity <= 0
      || !Number.isSafeInteger(linePriceCents) || linePriceCents < 0
      || !item.sku || !item.title || !item.productId) {
      throw new Error('Invalid cart item');
    }
    const baseUnitPriceCents = Math.floor(linePriceCents / quantity);
    const remainder = linePriceCents % quantity;
    const itemFor = (splitQuantity, splitUnitPriceCents) => ({
      productId: String(item.productId),
      quantity: splitQuantity,
      sku: String(item.sku),
      title: String(item.title),
      unitPrice: (splitUnitPriceCents / 100).toFixed(2),
      variantId: item.variantId ? String(item.variantId) : null,
    });
    return [
      ...(remainder > 0 ? [itemFor(remainder, baseUnitPriceCents + 1)] : []),
      ...(quantity - remainder > 0
        ? [itemFor(quantity - remainder, baseUnitPriceCents)]
        : []),
    ];
  });

  const pageUrl = new URL(context.locationUrl);
  pageUrl.hash = '';
  pageUrl.search = '';
  return {
    attribution: attribution(
      context.consent,
      context.locationUrl,
      context.storage,
      context.cookieSource,
    ),
    consent: context.consent,
    currency: dataset.currency,
    idempotencyKey: context.idempotencyKey,
    items: normalizedItems,
    pageUrl: pageUrl.href,
    shopifyCartId: null,
    shopifyCartToken: dataset.cartToken || null,
    sourceSurface: 'cart',
    totalAmount: (totalPriceCents / 100).toFixed(2),
  };
}

function discountedLinePriceCents(item) {
  const originalLinePrice = Number.parseInt(item.original_line_price, 10);
  if (!Number.isSafeInteger(originalLinePrice) || originalLinePrice < 0) {
    return Number.parseInt(item.final_line_price, 10);
  }
  const allocatedDiscount = Array.isArray(item.discount_allocations)
    ? item.discount_allocations.reduce((total, allocation) => {
      const amount = Number.parseInt(allocation.amount, 10);
      return total + (Number.isSafeInteger(amount) && amount > 0 ? amount : 0);
    }, 0)
    : 0;
  return Math.max(0, originalLinePrice - allocatedDiscount);
}

export function cartDataSnapshot(cartData) {
  if (!cartData || !Array.isArray(cartData.items)) throw new Error('Invalid cart data');
  const items = cartData.items.map((item) => {
    const quantity = Number.parseInt(item.quantity, 10);
    const linePriceCents = discountedLinePriceCents(item);
    if (!Number.isSafeInteger(quantity) || quantity <= 0
      || !Number.isSafeInteger(linePriceCents) || linePriceCents < 0) {
      throw new Error('Invalid cart data item');
    }
    return {
      image: item.image || null,
      linePriceCents,
      productId: item.product_id,
      productUrl: item.url || null,
      quantity,
      sku: item.sku || `shopify-${item.product_id}-${item.variant_id}`,
      title: item.product_title || item.title,
      unitPriceCents: Math.round(linePriceCents / quantity),
      variantId: item.variant_id || null,
    };
  });
  return {
    cartToken: cartData.token || null,
    currency: cartData.currency,
    items,
    primaryItem: [...items].sort((a, b) => b.linePriceCents - a.linePriceCents)[0] || null,
    totalPriceCents: Number.parseInt(cartData.total_price, 10),
  };
}

function syncCartRoot(root, cartData) {
  const snapshot = cartDataSnapshot(cartData);
  root.dataset.priceCents = String(snapshot.totalPriceCents);
  root.dataset.currency = snapshot.currency || '';
  root.dataset.cartToken = snapshot.cartToken || '';
  root.dataset.imageUrl = snapshot.primaryItem?.image || '';
  root.dataset.productUrl = snapshot.primaryItem?.productUrl
    ? new URL(snapshot.primaryItem.productUrl, window.location.origin).href
    : '';
  root.querySelector('[data-yourasset-items]').textContent = JSON.stringify(
    snapshot.items.map(({ image, productUrl, ...item }) => item),
  );
  root.hidden = snapshot.currency !== 'CHF'
    || !Number.isSafeInteger(snapshot.totalPriceCents)
    || snapshot.totalPriceCents <= YOURASSET_THRESHOLD_CENTS
    || snapshot.items.length === 0;
  renderFinancingPreview(root);
}

async function loadCartData() {
  const response = await fetch(`${globalThis.routes?.cart_url || '/cart'}.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
  return response.json();
}

export function isAllowedYourassetUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:'
      && (parsed.hostname === 'yourasset.com' || parsed.hostname.endsWith(YOURASSET_HOST_SUFFIX));
  } catch {
    return false;
  }
}

function readQuantity(root) {
  const section = root.closest('.shopify-section');
  const input = section?.querySelector('input[name="quantity"]');
  const quantity = Number.parseInt(input?.value || '1', 10);
  return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : 1;
}

function edgeTagPayload(result, payload) {
  return {
    event_id: result.attemptId,
    orderId: result.reference,
    ya_click_id: result.yaClickId,
    reference: result.reference,
    currency: payload.currency,
    value: Number(payload.totalAmount),
    contents: payload.items.map((item) => ({
      id: item.sku,
      item_price: Number(item.unitPrice),
      quantity: item.quantity,
    })),
    items: payload.items,
  };
}

export function emitTracking(result, payload) {
  if (typeof globalThis.edgetag !== 'function') return;
  const data = edgeTagPayload(result, payload);
  const options = { method: 'beacon' };
  const emit = (eventName) => {
    try {
      globalThis.edgetag('tag', eventName, data, {}, options);
    } catch (error) {
      console.warn(`[TBWL Yourasset] ${eventName} tracking failed`, error);
    }
  };
  if (payload.consent.analytics) {
    emit('YourassetCheckoutClicked');
  }
  if (payload.consent.marketing) {
    emit('InitiateCheckout');
  }
}

function createYourassetComponent(dataset, totalAmount, placement, reference = null) {
  const component = document.createElement('ya-checkout-financing');
  component.setAttribute('merchantid', dataset.merchantId);
  component.setAttribute('brand', dataset.brand);
  component.setAttribute('model', dataset.model);
  component.setAttribute('price', totalAmount);
  component.setAttribute('language', dataset.language);
  component.setAttribute('placement', placement);
  component.setAttribute('preowned', '');
  if (reference) component.setAttribute('reference', reference);
  if (dataset.productUrl) component.setAttribute('product-url', dataset.productUrl);
  if (dataset.imageUrl) component.setAttribute('image', dataset.imageUrl);
  return component;
}

export function financingPreviewLanguage(language) {
  // In the pinned 2.1.1 widget, no-SKU financing-details content is empty in
  // French and Italian. Keep provider-calculated details visible in English;
  // the actual checkout session still receives the storefront language.
  return language === 'de' ? 'de' : 'en';
}

export function financingPreviewVisible(root) {
  if (root.hidden) return false;
  if (root.dataset.sourceSurface !== 'cart_drawer') return true;
  return Boolean(root.closest('cart-drawer')?.classList.contains('active'));
}

function renderFinancingPreview(root) {
  const display = root.querySelector('[data-yourasset-display]');
  if (!display) return null;
  if (root.hidden) {
    display.replaceChildren();
    return null;
  }
  // A closed cart drawer is present on PDPs. Defer its provider session until
  // the drawer opens so it cannot race the visible PDP financing-details call.
  if (!financingPreviewVisible(root)) return display.firstElementChild;

  const priceCents = Number.parseInt(root.dataset.priceCents, 10);
  if (!Number.isSafeInteger(priceCents) || priceCents <= 0) {
    display.replaceChildren();
    return null;
  }

  const component = createYourassetComponent(
    { ...root.dataset, language: financingPreviewLanguage(root.dataset.language) },
    (priceCents / 100).toFixed(2),
    'checkout-compact',
  );
  component.setAttribute('theme', 'p-white');
  component.setAttribute('full-width', '');
  display.replaceChildren(component);
  return component;
}

function observeCartDrawer(root) {
  if (root.dataset.sourceSurface !== 'cart_drawer') return;
  const drawer = root.closest('cart-drawer');
  if (!drawer) return;
  const observer = new MutationObserver(() => {
    if (drawer.classList.contains('active')) renderFinancingPreview(root);
  });
  observer.observe(drawer, { attributeFilter: ['class'], attributes: true });
}

function createFinancingComponent(root, dataset, reference, totalAmount) {
  const component = createYourassetComponent(dataset, totalAmount, 'checkout', reference);
  root.querySelector('[data-yourasset-runtime]').replaceChildren(component);
  return component;
}

export function isYourassetCheckoutClick(event) {
  return event.composedPath().some((node) => (
    node?.tagName === 'A' && node.classList?.contains('ya-link')
  ));
}

export async function financingUrl(
  component,
  timeoutMs = 15_000,
  registry = globalThis.customElements,
) {
  const startedAt = Date.now();
  let rejectDeadline;
  const deadline = new Promise((_, reject) => {
    rejectDeadline = setTimeout(() => reject(new Error('Yourasset session timed out')), timeoutMs);
  });
  try {
    await Promise.race([registry.whenDefined('ya-checkout-financing'), deadline]);
    while (Date.now() - startedAt < timeoutMs) {
      const href = component.shadowRoot?.querySelector('a.ya-link')?.href;
      if (isAllowedYourassetUrl(href)) return href;
      await Promise.race([
        new Promise((resolve) => setTimeout(resolve, 50)),
        deadline,
      ]);
    }
    throw new Error('Yourasset session timed out');
  } finally {
    clearTimeout(rejectDeadline);
  }
}

export async function createAttempt(payload, timeoutMs = 10_000) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(APP_PROXY_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Attempt persistence failed (${response.status})`);
    const result = await response.json();
    if (!/^YA-[0-9A-F]{20}$/.test(result.reference || '')
      || typeof result.attemptId !== 'string'
      || typeof result.yaClickId !== 'string') {
      throw new Error('Attempt response was invalid');
    }
    return result;
  } finally {
    clearTimeout(timeout);
  }
}

function initialize(root) {
  // Section-rendered HTML can retain data attributes while event listeners are
  // lost when Shopify replaces a cart drawer node. Track the live node instead.
  if (initializedRoots.has(root)) return;
  initializedRoots.add(root);
  root.dataset.yourassetInitialized = 'true';
  const status = root.querySelector('[data-yourasset-status]');
  let inFlight = false;
  let idempotencyKey = null;
  let attemptFingerprint = null;

  renderFinancingPreview(root);
  observeCartDrawer(root);
  root.addEventListener('click', async (event) => {
    if (!isYourassetCheckoutClick(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (inFlight) return;
    inFlight = true;
    const popup = window.open('about:blank', '_blank');
    if (popup) popup.opener = null;
    root.setAttribute('aria-busy', 'true');
    status.hidden = false;
    status.dataset.error = 'false';
    status.textContent = root.dataset.loadingLabel;

    try {
      const consentState = consent(await loadCustomerPrivacy());
      const isCart = root.dataset.sourceSurface === 'cart' || root.dataset.sourceSurface === 'cart_drawer';
      if (isCart) syncCartRoot(root, await loadCartData());
      if (root.hidden) throw new Error('Cart is not eligible for Yourasset');
      const dataset = { ...root.dataset };
      const quantity = isCart ? null : readQuantity(root);
      const items = isCart
        ? JSON.parse(root.querySelector('[data-yourasset-items]')?.textContent || '[]')
        : null;
      const nextFingerprint = JSON.stringify(isCart ? [
        dataset.sourceSurface,
        dataset.priceCents,
        dataset.cartToken,
        items,
        window.location.href,
      ] : [
        'pdp',
        dataset.productId,
        dataset.variantId,
        dataset.priceCents,
        quantity,
        window.location.href,
      ]);
      if (nextFingerprint !== attemptFingerprint) {
        idempotencyKey = uuid();
        attemptFingerprint = nextFingerprint;
      }
      const context = {
        consent: consentState,
        cookieSource: document.cookie,
        idempotencyKey,
        locationUrl: window.location.href,
        storage: window.sessionStorage,
      };
      const payload = isCart
        ? buildCartAttemptPayload(dataset, items, context)
        : buildAttemptPayload(dataset, quantity, context);
      const result = await createAttempt(payload);
      const component = createFinancingComponent(root, dataset, result.reference, payload.totalAmount);
      const url = await financingUrl(component);
      emitTracking(result, payload);
      if (popup && !popup.closed) popup.location.replace(url);
      else window.location.assign(url);
    } catch (error) {
      popup?.close();
      status.dataset.error = 'true';
      status.textContent = root.dataset.errorLabel;
      root.removeAttribute('aria-busy');
      inFlight = false;
      console.error('[TBWL Yourasset] Checkout start failed', error);
    }
  }, { capture: true });
}

function syncVariant(root, source) {
  const initialized = root.dataset.yourassetInitialized;
  Object.keys(root.dataset).forEach((key) => delete root.dataset[key]);
  Object.assign(root.dataset, source.dataset);
  root.dataset.yourassetInitialized = initialized;
  root.hidden = source.hidden;
  renderFinancingPreview(root);
}

function subscribeToVariantChanges() {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    document.querySelectorAll('[data-yourasset-checkout]').forEach((root) => {
      if (root.dataset.sectionId !== data.sectionId) return;
      const source = [...data.html.querySelectorAll('[data-yourasset-checkout]')]
        .find((candidate) => candidate.dataset.sectionId === data.sectionId);
      if (source) syncVariant(root, source);
      else root.hidden = true;
    });
  });
}

function subscribeToCartChanges() {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
    if (cartData?.items) {
      document.querySelectorAll('[data-yourasset-checkout][data-source-surface^="cart"]')
        .forEach((root) => syncCartRoot(root, cartData));
    }
    requestAnimationFrame(() => initializeYourassetCheckout());
  });
}

export function initializeYourassetCheckout(scope = document) {
  if (scope.matches?.('[data-yourasset-checkout]')) initialize(scope);
  scope.querySelectorAll('[data-yourasset-checkout]').forEach(initialize);
}

function observeCheckoutRoots() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initializeYourassetCheckout(node);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (typeof document !== 'undefined') {
  initializeYourassetCheckout();
  subscribeToVariantChanges();
  subscribeToCartChanges();
  observeCheckoutRoots();
  document.addEventListener('shopify:section:load', (event) => initializeYourassetCheckout(event.target));
}
